import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { collectiveReadings } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CollectiveJune2026Page = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        source: 'collective-june-2026'
      });
      setSubscribed(true);
      setEmail('');
      toast.success('You\'re subscribed', {
        description: 'You\'ll receive each month\'s reflection in your inbox.'
      });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again.'
      });
    }
    setIsSubmitting(false);
  };

  const previousReadings = collectiveReadings.filter(r => r.slug !== 'june-2026');

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO 
        title="June 2026 Collective Reflection | The Soul Script"
        description="June 2026 reflection exploring patterns, boundaries, and conscious action."
        path="/collective/june-2026"
      />

      {/* Full-Width Hero */}
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1560563609-3b4b1f5c2122?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/75 to-[var(--color-charcoal)]/15" />
        </div>
        
        <div className="relative w-full pb-16 pt-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Link 
                to="/collective" 
                className="inline-flex items-center gap-2 text-[var(--color-gold)] text-sm tracking-[0.2em] uppercase mb-8 hover:opacity-80 transition-opacity"
                data-testid="back-to-collective"
              >
                <ArrowLeft className="w-4 h-4" />
                The Collective
              </Link>
              
              <p className="text-[var(--color-gold)] text-lg tracking-[0.2em] uppercase mb-4">
                June 2026
              </p>
              
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.05]" data-testid="june-2026-title">
                When Patterns Become Visible
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before You Read */}
      <section className="py-16 bg-[var(--color-dark-purple)]/10 border-y border-[var(--color-gold)]/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <p className="text-[var(--color-cream)]/70 leading-[2] text-lg italic">
              Pause.<br /><br />
              Take one slow breath. Let your body settle.<br /><br />
              This reading is not a prediction. It is a mirror.<br /><br />
              Notice what resonates. Leave what does not.
            </p>
            <p className="text-[var(--color-gold)] text-lg mt-8 font-serif">
              Consider where you may already recognise a pattern in your life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* The Energy of June */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                The Energy of June
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>The central energy this month is represented by the Devil card from the Sacred Indian Tarot, with guidance from the Breathe card in the Wisdom of the Oracle.</p>
              <p>June brings clarity that is difficult to ignore. And that it begins right after the Blue Moon the day before, a huge time for intentional release or running the risk of repeating patterns.</p>
              <p className="text-[var(--color-cream)]">June draws attention to what binds you. Habits, attachments, and patterns that operate without much thought.</p>
              <p>There may be a pull toward excess or distraction. Short-term relief that comes at a cost.</p>
              <p className="text-[var(--color-cream)]">This is not about judgment. It is about recognition.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Seeing where you are acting automatically rather than choosing consciously.</p>
              <p>The Breathe card brings the counterbalance.</p>
              <p className="text-[var(--color-cream)]">Slow down.</p>
              <p>Clarity strengthens when you pause long enough to observe what is actually driving your behaviour.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/25 to-transparent" />
          </div>

          {/* What Is Being Released */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-bottle-green-light)] sticky top-32">
                What Is Being Released
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>The Time to Go card and the Bear archetype point to cycles that are ending.</p>
              <p>You may feel this in relationships, routines, or ways of working that no longer support you.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">This is a month to take stock.</p>
              <p>What has been repeating.</p>
              <p>What has served its purpose.</p>
              <p>What no longer needs to continue.</p>
              <p>The Bear brings attention to boundaries.</p>
              <p className="text-[var(--color-cream)]">Time, energy, and attention need to be used more deliberately.</p>
              <p>Not everything requires your involvement.</p>
              <p className="text-[var(--color-cream)]">Ending something does not diminish its value. It acknowledges that it has run its course.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/25 to-transparent" />
          </div>

          {/* What Is Being Invited */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                What Is Being Invited
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>The energy shifting in is more measured.</p>
              <p>It is represented by the Mesopotamian Goddess Ishtar card from the Goddess Power Oracle and the influence of the Full Moon in Scorpio cards from the Moonology Oracle by Yasmin Boland.</p>
              <p className="text-[var(--color-cream)]">This is not about control. It is about awareness.</p>
              <p>A more conscious relationship with timing, intuition, and the signals that guide your decisions.</p>
              <p>There is also a shift toward co-creation.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Recognising that your actions exist within a wider context. One that responds better to clarity than force.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/25 to-transparent" />
          </div>

          {/* Guidance */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-cream)]/70 sticky top-32">
                Guidance
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p className="text-[var(--color-cream)]">The guidance this month is practical.</p>
              <p>Ground Yourself reinforces the need to stabilise through the body and the environment.</p>
              <p className="text-[var(--color-cream)]">Slow down where you tend to rush.</p>
              <p className="text-[var(--color-cream)]">Act where you tend to avoid.</p>
              <p className="text-[var(--color-cream)]">Step away from what feels excessive or draining.</p>
              <p className="text-[var(--color-cream)]">Stay present with discomfort instead of escaping it.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Bring your attention back to the body.</p>
              <p>Walk. Move. Spend time outdoors.</p>
              <p>Engage in simple, physical activities that require presence.</p>
              <p>When the mind becomes restless, return to something tangible.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">This is how stability is rebuilt.</p>
            </div>
          </motion.section>

        </div>
      </article>

      {/* Inquiry */}
      <section className="py-20 lg:py-28 bg-[var(--color-bottle-green)]/10 border-y border-[var(--color-gold)]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Inquiry</h2>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[var(--color-cream)] leading-[1.4] mb-6">
              Notice where you are acting out of habit instead of conscious choice.
            </p>
            <p className="text-[var(--color-cream)]/60 text-lg leading-relaxed max-w-2xl mx-auto">
              Interrupt the pattern with one clear action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Closing</h2>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              June is direct.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              It shows you what is already present.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/85 leading-[1.8]">
              What you do with that awareness matters.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              You do not need to change everything at once.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/85 leading-[1.8]">
              But you do need to respond differently where it is clear.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              Pause. Observe. Adjust.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-[var(--color-cream)] mt-8">
              From there, your next step becomes more intentional.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Continue the Work */}
      <section className="py-20 bg-[var(--color-charcoal-light)] border-t border-[var(--color-gold)]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Tarot Session */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Continue the Work</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  If this month's reflection brought something into focus, you are welcome to explore it further through a reflective tarot session.
                </p>
                <p className="text-[var(--color-cream)]/60 leading-[1.8] mb-6 text-sm">
                  Tarot here is not predictive. It is a grounded conversation that supports clarity, emotional resilience, and conscious choice.
                </p>
                <Link
                  to="/tarot"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-full hover:bg-[var(--color-gold-light)] transition-colors"
                  data-testid="june-book-tarot-btn"
                >
                  Book a Tarot Session
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Newsletter */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Monthly Reflections</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  If you would like to receive each month's Collective Reflection directly in your inbox, you can leave your email below.
                </p>
                
                {subscribed ? (
                  <p className="text-[var(--color-gold)]" data-testid="june-subscribed-msg">Thank you. You'll receive the next reflection in your inbox.</p>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      required
                      className="w-full px-5 py-3 bg-[var(--color-charcoal)] border border-[var(--color-gold)]/20 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                      data-testid="collective-june-email"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-lg hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50"
                      data-testid="collective-june-submit"
                    >
                      {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </button>
                    <p className="text-xs text-[var(--color-cream)]/40 text-center">
                      One reflection per month. No noise.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Previous Reflections */}
      <section className="py-12 border-t border-[var(--color-gold)]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer py-4 text-[var(--color-cream)]/60 hover:text-[var(--color-cream)] transition-colors" data-testid="june-previous-reflections-toggle">
              <span className="font-serif text-lg">Previous Collective Reflections</span>
              <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="pt-4 pb-8 space-y-4">
              {previousReadings.length > 0 ? (
                previousReadings.map((reading) => (
                  <Link
                    key={reading.id}
                    to={`/collective/${reading.slug}`}
                    className="block p-4 bg-[var(--color-charcoal-light)] rounded-lg hover:bg-[var(--color-dark-purple)]/20 transition-colors"
                  >
                    <p className="text-[var(--color-gold)] text-sm mb-1">{reading.month}</p>
                    <p className="text-[var(--color-cream)]">{reading.title}</p>
                  </Link>
                ))
              ) : (
                <p className="text-[var(--color-cream)]/50 italic">No previous reflections yet.</p>
              )}
            </div>
          </details>
        </div>
      </section>
    </div>
  );
};
